import React, { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';

interface CollectionDeck {
  callsign: string;
  operatingArea: string;
  deckName: string;
  deckCategory: string;
  deckType: string;
  intDiscipline: string;
  priority: number;
  crType: string;
  targetId: string;
  targetName: string;
  coordinates: string;
}

interface ChangeLog {
  callsign: string;
  operatingArea: string;
  type: string;
  reason: string;
  justification: string;
  author: string;
  at: string;
}

interface SidePanelsProps {
  atoDate: string;
}

const COLORS = {
  bgDark: 'rgba(18, 19, 27, 0.9)',
  bgPanel: 'rgba(18, 19, 27, 0.94)',
  border: '#333',
  accent: '#f72bef',
  secondary: '#00b8ff',
  textPrimary: '#cacce5',
  textSecondary: '#8a90a0',
};

const INT_COLORS: { [key: string]: string } = {
  FMV: '#00e676',
  GEOINT: '#00b8ff',
  SIGINT: '#ff9100',
  MTI: '#ff1744',
  MASINT: '#d500f9',
};

const SidePanels: React.FC<SidePanelsProps> = ({ atoDate }) => {
  const [openPanel, setOpenPanel] = useState<'collection' | 'log' | null>(null);
  const [collectionDecks, setCollectionDecks] = useState<CollectionDeck[]>([]);
  const [changeLogs, setChangeLogs] = useState<ChangeLog[]>([]);
  const [loadingCollection, setLoadingCollection] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [errorCollection, setErrorCollection] = useState<string | null>(null);
  const [errorLogs, setErrorLogs] = useState<string | null>(null);

  // Fetch collection decks when panel opens or atoDate changes
  useEffect(() => {
    if (openPanel === 'collection' && atoDate) {
      setLoadingCollection(true);
      setErrorCollection(null);
      fetch(`/api/export/collection-decks?date=${atoDate}`)
        .then((res) => res.json())
        .then((data) => {
          setCollectionDecks(Array.isArray(data) ? data : []);
          setLoadingCollection(false);
        })
        .catch((err) => {
          setErrorCollection(err instanceof Error ? err.message : 'Unknown error');
          setLoadingCollection(false);
        });
    }
  }, [openPanel, atoDate]);

  // Fetch change logs when panel opens
  useEffect(() => {
    if (openPanel === 'log') {
      setLoadingLogs(true);
      setErrorLogs(null);
      fetch('/api/export/change-log')
        .then((res) => res.json())
        .then((data) => {
          setChangeLogs(Array.isArray(data) ? data.slice(0, 100) : []);
          setLoadingLogs(false);
        })
        .catch((err) => {
          setErrorLogs(err instanceof Error ? err.message : 'Unknown error');
          setLoadingLogs(false);
        });
    }
  }, [openPanel]);

  const getIntColor = (discipline: string): string => {
    return INT_COLORS[discipline] || '#c42ebe';
  };

  const groupedDecks = collectionDecks.reduce(
    (acc, deck) => {
      if (!acc[deck.callsign]) {
        acc[deck.callsign] = [];
      }
      acc[deck.callsign].push(deck);
      return acc;
    },
    {} as { [key: string]: CollectionDeck[] }
  );

  return (
    <>
      {/* Vertical Tab Strip - Right Edge */}
      <Box
        sx={{
          position: 'absolute',
          right: 0,
          top: '80px',
          zIndex: 1200,
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
        }}
      >
        {/* Collection Requirements Tab */}
        <Box
          onClick={() =>
            setOpenPanel(openPanel === 'collection' ? null : 'collection')
          }
          sx={{
            padding: '8px 4px',
            backgroundColor: COLORS.bgDark,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '4px 0 0 4px',
            cursor: 'pointer',
            fontSize: '0.6rem',
            letterSpacing: '0.05em',
            color: openPanel === 'collection' ? COLORS.accent : COLORS.textSecondary,
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
            whiteSpace: 'nowrap',
            userSelect: 'none',
            transition: 'color 0.2s',
            '&:hover': {
              color: COLORS.accent,
            },
          }}
        >
          COLLECTION REQUIREMENTS
        </Box>

        {/* Log Tab */}
        <Box
          onClick={() => setOpenPanel(openPanel === 'log' ? null : 'log')}
          sx={{
            padding: '8px 4px',
            backgroundColor: COLORS.bgDark,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '4px 0 0 4px',
            cursor: 'pointer',
            fontSize: '0.6rem',
            letterSpacing: '0.05em',
            color: openPanel === 'log' ? COLORS.accent : COLORS.textSecondary,
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
            whiteSpace: 'nowrap',
            userSelect: 'none',
            transition: 'color 0.2s',
            '&:hover': {
              color: COLORS.accent,
            },
          }}
        >
          LOG
        </Box>
      </Box>

      {/* Side Panel - Opens when tab is clicked */}
      {openPanel && (
        <Box
          sx={{
            position: 'absolute',
            right: '26px',
            top: '40px',
            bottom: '8px',
            width: '400px',
            zIndex: 1150,
            backgroundColor: COLORS.bgPanel,
            backdropFilter: 'blur(3px)',
            border: `1px solid ${COLORS.border}`,
            borderRadius: 1,
            padding: '12px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Panel Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px',
              paddingBottom: '8px',
              borderBottom: `1px solid ${COLORS.border}`,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.8rem',
                fontWeight: 'bold',
                color: COLORS.accent,
              }}
            >
              {openPanel === 'collection' ? 'COLLECTION REQUIREMENTS' : 'LOG'}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setOpenPanel(null)}
              sx={{
                color: COLORS.textSecondary,
                padding: '4px',
                '&:hover': {
                  color: COLORS.accent,
                },
              }}
            >
              ×
            </IconButton>
          </Box>

          {/* Collection Requirements Panel Content */}
          {openPanel === 'collection' && (
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
              {!atoDate ? (
                <Typography sx={{ fontSize: '0.7rem', color: COLORS.textSecondary }}>
                  Pick an ATO date to load collection requirements.
                </Typography>
              ) : loadingCollection ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                  <CircularProgress size={24} />
                </Box>
              ) : errorCollection ? (
                <Typography sx={{ fontSize: '0.7rem', color: '#ff4444' }}>
                  Error: {errorCollection}
                </Typography>
              ) : collectionDecks.length === 0 ? (
                <Typography sx={{ fontSize: '0.7rem', color: COLORS.textSecondary }}>
                  No requirements found.
                </Typography>
              ) : (
                <>
                  <Typography
                    sx={{
                      fontSize: '0.65rem',
                      color: COLORS.textSecondary,
                      marginBottom: '8px',
                      fontWeight: 'bold',
                    }}
                  >
                    {collectionDecks.length} requirements
                  </Typography>
                  {Object.entries(groupedDecks).map(([callsign, decks]) => (
                    <Box key={callsign} sx={{ marginBottom: '12px' }}>
                      <Typography
                        sx={{
                          fontSize: '0.65rem',
                          fontWeight: 'bold',
                          color: COLORS.textPrimary,
                          marginBottom: '6px',
                        }}
                      >
                        {callsign}
                      </Typography>
                      {decks.map((deck, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            fontSize: '0.6rem',
                            color: COLORS.textSecondary,
                            marginBottom: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            flexWrap: 'wrap',
                          }}
                        >
                          <Chip
                            label={deck.intDiscipline}
                            size="small"
                            sx={{
                              fontSize: '0.6rem',
                              backgroundColor: getIntColor(deck.intDiscipline),
                              color: '#000',
                              fontWeight: 'bold',
                              height: '18px',
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: '0.6rem',
                              color: COLORS.textSecondary,
                              minWidth: 'fit-content',
                            }}
                          >
                            P{deck.priority}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.6rem',
                              color: COLORS.textSecondary,
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            {deck.targetName || deck.targetId || deck.crType}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.55rem',
                              color: '#666',
                              minWidth: 'fit-content',
                            }}
                          >
                            {deck.coordinates}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  ))}
                </>
              )}
            </Box>
          )}

          {/* Log Panel Content */}
          {openPanel === 'log' && (
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
              {loadingLogs ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                  <CircularProgress size={24} />
                </Box>
              ) : errorLogs ? (
                <Typography sx={{ fontSize: '0.7rem', color: '#ff4444' }}>
                  Error: {errorLogs}
                </Typography>
              ) : changeLogs.length === 0 ? (
                <Typography sx={{ fontSize: '0.7rem', color: COLORS.textSecondary }}>
                  No log entries found.
                </Typography>
              ) : (
                <>
                  {changeLogs.length >= 100 && (
                    <Typography
                      sx={{
                        fontSize: '0.65rem',
                        color: COLORS.textSecondary,
                        marginBottom: '8px',
                        fontStyle: 'italic',
                      }}
                    >
                      Showing first 100 entries
                    </Typography>
                  )}
                  {changeLogs.map((log, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        marginBottom: '12px',
                        paddingBottom: '8px',
                        borderBottom: `1px solid ${COLORS.border}`,
                        '&:last-child': {
                          borderBottom: 'none',
                          marginBottom: 0,
                          paddingBottom: 0,
                        },
                      }}
                    >
                      {/* Line 1: Type chip + Callsign bold + Author right-aligned */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          marginBottom: '4px',
                        }}
                      >
                        <Chip
                          label={log.type}
                          size="small"
                          sx={{
                            fontSize: '0.6rem',
                            backgroundColor: '#ff9100',
                            color: '#000',
                            fontWeight: 'bold',
                            height: '18px',
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: '0.65rem',
                            fontWeight: 'bold',
                            color: COLORS.textPrimary,
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {log.callsign}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.55rem',
                            color: COLORS.textSecondary,
                            minWidth: 'fit-content',
                          }}
                        >
                          {log.author}
                        </Typography>
                      </Box>

                      {/* Line 2: Reason + Justification */}
                      <Typography
                        sx={{
                          fontSize: '0.6rem',
                          color: COLORS.textSecondary,
                          marginBottom: '4px',
                        }}
                      >
                        {log.reason}
                        {log.justification && ` (${log.justification})`}
                      </Typography>

                      {/* Line 3: Formatted timestamp */}
                      <Typography
                        sx={{
                          fontSize: '0.55rem',
                          color: '#666',
                        }}
                      >
                        {Number.isNaN(Date.parse(log.at))
                          ? (log.at || '—')
                          : `${new Date(log.at).toISOString().slice(0, 16).replace('T', ' ')}Z`}
                      </Typography>
                    </Box>
                  ))}
                </>
              )}
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default SidePanels;
